import { createContext, useContext, useSyncExternalStore } from "react";

import type { SortKey } from "@/domains/documents/utils/documentSorting";

/**
 * Which date the document list's one date column shows: when the file was
 * uploaded to Franklin, or the modification date in the file's own metadata.
 *
 * A per-viewer preference shared by the header and every row (and every list
 * on the page), so it lives in one module-level slot rather than in a
 * component: the header switches it and the rows re-render from the same value.
 */
export type DocumentListDateBasis = "upload" | "modified";

export const DOCUMENT_LIST_DATE_BASIS_STORAGE_KEY = "documents-listDateBasis";

/** The sort key that orders rows by the date the column shows. */
export const DATE_BASIS_SORT_KEY: Record<DocumentListDateBasis, SortKey> = {
  upload: "created_at",
  modified: "modified_at",
};

/** The basis a date sort key belongs to, or null for a non-date key. */
export function dateBasisForSortKey(key: SortKey | undefined): DocumentListDateBasis | null {
  if (key === "created_at") return "upload";
  if (key === "modified_at") return "modified";
  return null;
}

function loadStoredBasis(): DocumentListDateBasis {
  try {
    return localStorage.getItem(DOCUMENT_LIST_DATE_BASIS_STORAGE_KEY) === "modified"
      ? "modified"
      : "upload";
  } catch {
    return "upload";
  }
}

let current: DocumentListDateBasis | null = null;
const listeners = new Set<() => void>();

function getSnapshot(): DocumentListDateBasis {
  if (current === null) current = loadStoredBasis();
  return current;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The viewer's current choice, outside React — e.g. to seed a list's initial sort. */
export function getDocumentListDateBasis(): DocumentListDateBasis {
  return getSnapshot();
}

export function setDocumentListDateBasis(next: DocumentListDateBasis): void {
  if (getSnapshot() === next) return;
  current = next;
  try {
    localStorage.setItem(DOCUMENT_LIST_DATE_BASIS_STORAGE_KEY, next);
  } catch {
    // A per-viewer convenience: an unwritable store only forgets the choice.
  }
  listeners.forEach((listener) => listener());
}

/** Test seam: forget the cached value so the next read goes back to storage. */
export function resetDocumentListDateBasisForTests(): void {
  current = null;
}

/** The viewer's stored choice, whatever list it is read for. */
export function useStoredDocumentListDateBasis(): DocumentListDateBasis {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * Pins a subtree's lists to one date, hiding the switch — for a list whose rows
 * carry no file-metadata dates (the share page), where "File modified" would
 * only relabel upload dates.
 *
 * Only for a list with no DocumentFiltersProvider above it: the provider sits
 * above any pin, cannot read it, and orders a date sort by the viewer's stored
 * choice. A pinned list under a provider would sort by a date it does not show.
 */
export const FixedDocumentListDateBasis = createContext<DocumentListDateBasis | null>(null);

/** Whether this list's date column is pinned (no switch in its header). */
export function useIsDocumentListDateBasisFixed(): boolean {
  return useContext(FixedDocumentListDateBasis) !== null;
}

/** The date this list's column shows: a pinned one, else the viewer's choice. */
export function useDocumentListDateBasis(): DocumentListDateBasis {
  const fixed = useContext(FixedDocumentListDateBasis);
  const stored = useStoredDocumentListDateBasis();
  return fixed ?? stored;
}
